import { List, Record } from "immutable";

export interface IFeneTechLineItemOptionData {
  optionGroup: string;
  description: string;
  optionValue: string;
}

export interface IFeneTechLineItemDetailData {
  odKey: number;
  quantity: number;
  displayWidth: number;
  displayHeight: number;
  unitPrice: number;
  formattedPart: string;
  thumbnailImageExists: boolean;
}

export interface IFeneTechLineItemData {
  lineItem: IFeneTechLineItemDetailData;
  options: IFeneTechLineItemOptionData[];
  subLineItems: IFeneTechLineItemData[];
}

export interface IFeneTechQuoteFullData {
  quote: { oKey: number };
  permissions: { imageAccessKey: number };
  lineItems: IFeneTechLineItemData[];
}

const defaultOptionProps: IFeneTechLineItemOptionData = {
  optionGroup: "",
  description: "",
  optionValue: "",
};

export class FeneTechLineItemOptionRecord extends Record(defaultOptionProps) implements IFeneTechLineItemOptionData {}

const defaultDetailProps: IFeneTechLineItemDetailData = {
  odKey: 0,
  quantity: 0,
  displayWidth: 0,
  displayHeight: 0,
  unitPrice: 0,
  formattedPart: "",
  thumbnailImageExists: false,
};

export class FeneTechLineItemDetailRecord extends Record(defaultDetailProps) implements IFeneTechLineItemDetailData {}

export interface IFeneTechLineItemRecord {
  lineItem: FeneTechLineItemDetailRecord;
  options: List<FeneTechLineItemOptionRecord>;
  subLineItems: List<FeneTechLineItemRecord>;
}

const defaultLineItemProps: IFeneTechLineItemRecord = {
  lineItem: new FeneTechLineItemDetailRecord(),
  options: List<FeneTechLineItemOptionRecord>(),
  subLineItems: List<FeneTechLineItemRecord>(),
};

export class FeneTechLineItemRecord extends Record(defaultLineItemProps) implements IFeneTechLineItemRecord {}

export interface IFeneTechQuoteFullRecord {
  oKey: number;
  imageAccessKey: number;
  lineItems: List<FeneTechLineItemRecord>;
}

const defaultQuoteFullProps: IFeneTechQuoteFullRecord = {
  oKey: 0,
  imageAccessKey: 0,
  lineItems: List<FeneTechLineItemRecord>(),
};

export class FeneTechQuoteFullRecord extends Record(defaultQuoteFullProps) implements IFeneTechQuoteFullRecord {}

export const feneTechLineItemFromJSON = (json: IFeneTechLineItemData): FeneTechLineItemRecord =>
  new FeneTechLineItemRecord({
    lineItem: new FeneTechLineItemDetailRecord({ ...json.lineItem }),
    options: List((json.options || []).map((option) => new FeneTechLineItemOptionRecord({ ...option }))),
    subLineItems: List((json.subLineItems || []).map(feneTechLineItemFromJSON)),
  });

/**
 * Hydrate a raw FeneTech QuoteFull JSON response into an Immutable record.
 *
 * The nested `quote` and `permissions` wrappers are flattened to `oKey` / `imageAccessKey`;
 * line items (and their recursive sub-line items) become Lists of Records. Image blobs are
 * intentionally NOT part of this record — they are transient binary and stay out of Redux.
 */
export const feneTechQuoteFullFromJSON = (json: IFeneTechQuoteFullData): FeneTechQuoteFullRecord =>
  new FeneTechQuoteFullRecord({
    oKey: json.quote?.oKey ?? 0,
    imageAccessKey: json.permissions?.imageAccessKey ?? 0,
    lineItems: List((json.lineItems || []).map(feneTechLineItemFromJSON)),
  });
